import type { IntegrationConnectorType, IntegrationPublic } from '@marketing-hub/shared';
import { INTEGRATION_CONNECTOR_LABELS } from '@marketing-hub/shared';

export const CONNECTOR_META: Record<
  IntegrationConnectorType,
  {
    title: string;
    description: string;
    avatarClass: 'ga4' | 'gsc' | 'ads' | 'psi' | 'brevo' | 'wp';
  }
> = {
  ga4: {
    title: INTEGRATION_CONNECTOR_LABELS.ga4,
    description: 'Sessies, gebruikers en conversies uit GA4.',
    avatarClass: 'ga4',
  },
  gsc: {
    title: INTEGRATION_CONNECTOR_LABELS.gsc,
    description: 'Klikken, impressies en top queries.',
    avatarClass: 'gsc',
  },
  pagespeed: {
    title: INTEGRATION_CONNECTOR_LABELS.pagespeed,
    description: 'Core Web Vitals en performance scores.',
    avatarClass: 'psi',
  },
  google_ads: {
    title: INTEGRATION_CONNECTOR_LABELS.google_ads,
    description: 'Campagne- en accountmetrics (read-only).',
    avatarClass: 'ads',
  },
  brevo: {
    title: INTEGRATION_CONNECTOR_LABELS.brevo,
    description: 'Transactional e-mail, webhooks (opens/clicks/bounces) en blocked contacts.',
    avatarClass: 'brevo',
  },
  wordpress: {
    title: INTEGRATION_CONNECTOR_LABELS.wordpress,
    description: 'REST-koppeling: content-tellingen sync + leads via inbound API.',
    avatarClass: 'wp',
  },
};

const STATUS_LABELS: Record<IntegrationPublic['status'], string> = {
  pending: 'In afwachting',
  active: 'Actief',
  error: 'Fout',
  disabled: 'Uitgeschakeld',
};

export function integrationStatusLabel(status: IntegrationPublic['status']): string {
  return STATUS_LABELS[status] ?? status;
}

export function connectorShortLabel(type: IntegrationConnectorType): string {
  switch (type) {
    case 'ga4':
      return 'GA4';
    case 'gsc':
      return 'GSC';
    case 'google_ads':
      return 'Ads';
    case 'pagespeed':
      return 'PSI';
    case 'brevo':
      return 'Br';
    case 'wordpress':
      return 'WP';
    default:
      return type;
  }
}

export type PermissionFixHint = {
  title: string;
  steps: string[];
};

export function permissionFixHint(
  connector: IntegrationConnectorType,
  lastError: string | null,
  config: Record<string, unknown>,
): PermissionFixHint | null {
  if (!lastError) return null;
  const err = lastError.toLowerCase();
  const isPermission =
    err.includes('permission') || err.includes('permission_denied') || err.includes('403');
  if (!isPermission) return null;

  const email =
    typeof config.service_account_email === 'string' ? config.service_account_email : null;

  if (connector === 'ga4') {
    const propertyId = config.property_id != null ? String(config.property_id) : '—';
    return {
      title: 'GA4: service account heeft geen toegang tot deze property',
      steps: [
        email
          ? `Noteer het service account: ${email}`
          : 'Open je service account JSON en kopieer client_email.',
        'In Google Analytics: Admin → Property → Property access management.',
        `Voeg het service account toe met minimaal rol Viewer (property ID: ${propertyId}).`,
        'Zorg dat de Analytics Data API aan staat in Google Cloud (zelfde project als de key).',
        'Klik hierna op Sync now — sync start ook automatisch na opslaan van credentials.',
      ],
    };
  }

  if (connector === 'gsc') {
    const siteUrl = typeof config.site_url === 'string' ? config.site_url : '—';
    return {
      title: 'Search Console: geen rechten op deze property',
      steps: [
        email
          ? `Service account: ${email}`
          : 'Gebruik het e-mailadres uit client_email in je JSON-key.',
        `In Search Console: instellingen → Gebruikers en rechten voor exact deze URL: ${siteUrl}`,
        'URL moet 1-op-1 overeenkomen (met/zonder www, trailing slash, http vs https).',
        'Geef minimaal Beperkte of Volledige rechten.',
        'Na toevoegen: Sync now (of opnieuw credentials opslaan).',
      ],
    };
  }

  return null;
}

export function formatIntegrationError(raw: string): string {
  const withoutCode = raw.replace(/^\d+\s+[A-Z_]+:\s*/i, '');
  return withoutCode.length > 280 ? `${withoutCode.slice(0, 277)}…` : withoutCode;
}
